/**
 * SPRINT V3 — Auto Referral injector.
 * Appends `?ref=<code>` + utm tracking to ANY URL shared from the app.
 * Pure client helper, no network calls. Reuses ss2026_ref_code from
 * `lib/referrals.ts` (do NOT generate a new code here).
 *
 * SPRINT V3.1 — also canonicalizes the host to https://versesport.com so
 * links shared from the Lovable preview iframe never leak the preview host.
 */
const REF_KEY = "ss2026_ref_code";
const CANONICAL_HOST = "versesport.com";
const PREVIEW_HOST_RE = /(lovable\.app|lovableproject\.com|localhost|127\.0\.0\.1)$/i;

export function readReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

export interface AutoReferralOpts {
  source?: string;   // e.g. "challenge", "quiniela", "hashtag"
  campaign?: string; // e.g. "v3_viral"
  medium?: string;   // e.g. "whatsapp"
}

/**
 * Append referral + utm params to a URL without clobbering existing params.
 * Returns the original URL untouched when no referral code is available.
 */
export function withAutoReferral(rawUrl: string, opts: AutoReferralOpts = {}): string {
  if (!rawUrl) return rawUrl;
  const code = readReferralCode();
  let url: URL;
  try {
    url = new URL(rawUrl, `https://${CANONICAL_HOST}`);
  } catch {
    return rawUrl;
  }
  forceCanonicalHost(url);
  if (code && !url.searchParams.has("ref")) url.searchParams.set("ref", code);
  applyUtm(url, opts);
  return url.toString();
}

function appendUtm(rawUrl: string, opts: AutoReferralOpts): string {
  try {
    const url = new URL(rawUrl, `https://${CANONICAL_HOST}`);
    forceCanonicalHost(url);
    applyUtm(url, opts);
    return url.toString();
  } catch {
    return rawUrl;
  }
}

function forceCanonicalHost(url: URL) {
  if (PREVIEW_HOST_RE.test(url.hostname) || url.hostname === "" || url.hostname === CANONICAL_HOST) {
    url.protocol = "https:";
    url.host = CANONICAL_HOST;
  }
}

function applyUtm(url: URL, opts: AutoReferralOpts) {
  if (opts.source && !url.searchParams.has("utm_source")) url.searchParams.set("utm_source", opts.source);
  if (opts.medium && !url.searchParams.has("utm_medium")) url.searchParams.set("utm_medium", opts.medium);
  if (opts.campaign && !url.searchParams.has("utm_campaign")) url.searchParams.set("utm_campaign", opts.campaign);
}
